import { getAllRouteStatisticsOptions, getAllActiveRoutesOptions } from '@/api/main'
import { RouteStatistics } from '@/api/main/types.gen'
import PageShell from '@/components/Common/PageShell'
import { useGenericTable } from '@/components/Table/useGenericTable'

import { requirePermission } from '@/utils/auth'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { round } from 'lodash-es'
import { useMemo } from 'react'
import { GenericTable, TableColumn } from '@/components/Table/GenericTable'
import { useDebouncedFilter } from '@/components/Table/useDebouncedFilter'
import { createRouteNameMap } from '@/utils/routes'
import ErrorState from '@/components/Common/ErrorState'
import { QueryBoundary } from '@/components/Common/QueryBoundary'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'

type EnhancedRouteStatistics = RouteStatistics & {
  name?: string
  overhead_ratio: number
}

export const Route = createFileRoute('/routes')({
  beforeLoad: () => requirePermission('routes'),
  component: () => (
    <QueryBoundary
      loadingFallback={<RoutesLoadingFallback />}
      errorTitle={i18next.t('failedToLoadRoutes', { ns: 'routeManagement' })}
      errorMessage={i18next.t('unableToLoadRoutes', { ns: 'routeManagement' })}
      pageTitle={i18next.t('routes', { ns: 'routeManagement' })}
      backButtonHref="/routes"
    >
      <RoutesPage />
    </QueryBoundary>
  ),
  errorComponent: ({ error }) => (
    <PageShell title={i18next.t('errorLoadingRoutes', { ns: 'routeManagement' })}>
      <ErrorState
        title={i18next.t('failedToLoadRoutes', { ns: 'routeManagement' })}
        message={error.message || i18next.t('unableToLoadRoutes', { ns: 'routeManagement' })}
      />
    </PageShell>
  ),
})

function RoutesLoadingFallback() {
  const { t } = useTranslation('routeManagement')

  const defaultColumns: TableColumn<EnhancedRouteStatistics>[] = [
    { key: 'name', label: t('routeName'), visible: true },
    { key: 'route_id', label: t('routeId'), visible: true },
    { key: 'route_version', label: t('routeVersion'), visible: true },
    { key: 'total_km', label: t('routeLength'), visible: true, align: 'right' },
    { key: 'ctrl_km', label: t('ctrlKm'), visible: false, align: 'right' },
    { key: 'overhead_ratio', label: t('overheadRatio'), visible: false, align: 'right' },
    { key: 'overhead_km', label: t('overheadKm'), visible: false, align: 'right' },
    { key: 'desc_km', label: t('descKm'), visible: false, align: 'right' },
  ]

  return (
    <PageShell title={t('routes')}>
      <GenericTable
        stickyHeader
        data={[]}
        isLoading={true}
        error={null}
        currentPage={1}
        onPageChange={() => {}}
        onFilterFieldChange={() => {}}
        onSearchChange={() => {}}
        getRowKey={() => ''}
        emptyMessage={t('noRoutesFound')}
        paginatedData={[]}
        filteredData={[]}
        itemsPerPage={Infinity}
        search=""
        columns={defaultColumns}
        onColumnsChange={() => {}}
        filterField={undefined}
        isDebouncing={false}
        hideCsvDownload={false}
        csvFileName="routes.csv"
        sortDirection="asc"
        onSortDirectionChange={() => {}}
        sortField="route_id"
        onSortFieldChange={() => {}}
      />
    </PageShell>
  )
}

function RoutesPage() {
  const { t } = useTranslation('routeManagement')
  const { data: routeStatistics } = useSuspenseQuery(getAllRouteStatisticsOptions())
  const { data: allRoutes } = useSuspenseQuery(getAllActiveRoutesOptions())

  const routeNameMap = useMemo(() => createRouteNameMap(allRoutes), [allRoutes])

  const enhancedData = useMemo(
    () =>
      routeStatistics?.map(item => {
        const key = `${item.route_id}-${item.route_version}`
        const name = routeNameMap.get(key)
        return {
          ...item,
          overhead_ratio: round(item.overhead_km / item.total_km, 2),
          total_km: round(item.total_km, 2),
          ctrl_km: round(item.ctrl_km, 2),
          desc_km: round(item.desc_km, 2),
          overhead_km: round(item.overhead_km, 2),
          name,
        }
      }) || [],
    [routeStatistics, routeNameMap]
  )

  const { filteredData, handleFilterFieldChange, handleSearchChange, search, filterField, isDebouncing } =
    useDebouncedFilter({ data: enhancedData })

  const {
    currentPage,
    tableColumns,
    paginatedData,
    handlePageChange,
    setTableColumns,
    sortDirection,
    setSortDirection,
    sortField,
    setSortField,
  } = useGenericTable({
    itemsPerPage: Infinity,
    defaultSortField: 'route_id',
    defaultSortDirection: 'asc',
    tableKey: 'routesTableColumns',
    columns: [
      { key: 'name', label: t('routeName'), visible: true },
      { key: 'route_id', label: t('routeId'), visible: true },
      { key: 'route_version', label: t('routeVersion'), visible: true },
      { key: 'total_km', label: t('routeLength'), visible: true, align: 'right' },
      { key: 'ctrl_km', label: t('ctrlKm'), visible: false, align: 'right' },
      { key: 'overhead_ratio', label: t('overheadRatio'), visible: false, align: 'right' },
      { key: 'overhead_km', label: t('overheadKm'), visible: false, align: 'right' },
      { key: 'desc_km', label: t('descKm'), visible: false, align: 'right' },
    ] as TableColumn<EnhancedRouteStatistics>[],
    searchDebounceMs: 500,
    filteredData,
  })

  const columnsWithRenderers = tableColumns.map(col => {
    if (col.key === 'route_id')
      return {
        ...col,
        render: (item: EnhancedRouteStatistics) => (
          <Link
            to="/route/$routeId/$version"
            params={{ routeId: item.route_id.toString(), version: item.route_version.toString() }}
            style={{
              color: 'var(--mantine-color-blue-6)',
              textDecoration: 'underline',
            }}
          >
            {item.route_id}
          </Link>
        ),
      }

    if (col.key === 'total_km') {
      return {
        ...col,
        render: (item: EnhancedRouteStatistics) => round(item.total_km, 1),
      }
    }

    if (col.key === 'ctrl_km') {
      return {
        ...col,
        render: (item: EnhancedRouteStatistics) => round(item.ctrl_km, 1),
      }
    }

    if (col.key === 'overhead_km')
      return {
        ...col,
        render: (item: EnhancedRouteStatistics) => round(item.overhead_km, 1),
      }

    if (col.key === 'desc_km') {
      return {
        ...col,
        render: (item: EnhancedRouteStatistics) => round(item.desc_km, 1),
      }
    }

    return col
  })

  return (
    <PageShell title={t('routes')}>
      <GenericTable
        stickyHeader
        data={enhancedData}
        isLoading={false}
        error={null}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        onFilterFieldChange={handleFilterFieldChange}
        onSearchChange={handleSearchChange}
        getRowKey={item => item.route_id}
        emptyMessage={t('noRoutesFound')}
        paginatedData={paginatedData}
        filteredData={filteredData}
        itemsPerPage={Infinity}
        search={search}
        columns={columnsWithRenderers}
        onColumnsChange={setTableColumns}
        filterField={filterField as keyof EnhancedRouteStatistics | undefined}
        isDebouncing={isDebouncing}
        hideCsvDownload={false}
        csvFileName="routes.csv"
        sortDirection={sortDirection}
        onSortDirectionChange={setSortDirection}
        sortField={sortField}
        onSortFieldChange={setSortField}
      />
    </PageShell>
  )
}
